// MCHD Room Scheduler - Building Management API Endpoints
// API calls for building management operations

import { TokenManager } from '../interceptors';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// TYPES
// =============================================================================

export interface Building {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BuildingWithStats extends Building {
  _count: {
    floors: number;
  };
  roomsCount?: number;
  holidaysCount?: number;
}

export interface CreateBuildingData {
  name: string;
  description?: string | null;
  isActive?: boolean;
}

export interface UpdateBuildingData {
  name?: string;
  description?: string | null;
  isActive?: boolean;
}

export interface BuildingStats {
  floorsCount: number;
  roomsCount: number;
  holidaysCount: number;
  activeRoomsCount: number;
  inactiveRoomsCount: number;
}

export interface GetBuildingsParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface GetBuildingsResponse {
  success: boolean;
  data?: {
    buildings: BuildingWithStats[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetBuildingResponse {
  success: boolean;
  data?: {
    building: BuildingWithStats;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface CreateBuildingResponse {
  success: boolean;
  data?: {
    building: BuildingWithStats;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface UpdateBuildingResponse {
  success: boolean;
  data?: {
    building: BuildingWithStats;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface DeleteBuildingResponse {
  success: boolean;
  data?: {
    building: BuildingWithStats;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetBuildingStatsResponse {
  success: boolean;
  data?: {
    stats: BuildingStats;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetBuildingsForSelectionResponse {
  success: boolean;
  data?: {
    buildings: Array<{
      id: string;
      name: string;
      description: string | null;
      isActive: boolean;
    }>;
  };
  error?: {
    code: string;
    message: string;
  };
}

// =============================================================================
// API UTILITIES
// =============================================================================

/**
 * Get authorization headers for API requests
 * @returns Headers object with authorization
 */
function getAuthHeaders(): HeadersInit {
  const token = TokenManager.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Make authenticated API request
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @returns Promise with response data
 */
async function apiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network request failed',
      },
    }));
    throw new Error(errorData.error?.message || 'API request failed');
  }

  return response.json();
}

/**
 * Build query string from parameters
 * @param params - Query parameters
 * @returns Query string
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
}

// =============================================================================
// BUILDING API FUNCTIONS
// =============================================================================

/**
 * Get all buildings with pagination and filtering
 * @param params - Query parameters
 * @returns Promise with buildings list
 */
export async function getBuildings(params: GetBuildingsParams = {}): Promise<GetBuildingsResponse> {
  const queryString = buildQueryString(params);
  const url = `${API_BASE_URL}/buildings${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<GetBuildingsResponse>(url);
}

/**
 * Get building by ID
 * @param id - Building ID
 * @returns Promise with building details
 */
export async function getBuildingById(id: string): Promise<GetBuildingResponse> {
  const url = `${API_BASE_URL}/buildings/${id}`;
  
  return apiRequest<GetBuildingResponse>(url);
}

/**
 * Create a new building
 * @param data - Building creation data
 * @returns Promise with created building
 */
export async function createBuilding(data: {
  name: string;
  description?: string | null;
  isActive?: boolean;
}): Promise<CreateBuildingResponse> {
  const url = `${API_BASE_URL}/buildings`;
  
  return apiRequest<CreateBuildingResponse>(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update building by ID
 * @param id - Building ID
 * @param data - Building update data
 * @returns Promise with updated building
 */
export async function updateBuilding(
  id: string,
  data: {
    name?: string;
    description?: string | null;
    isActive?: boolean;
  }
): Promise<UpdateBuildingResponse> {
  const url = `${API_BASE_URL}/buildings/${id}`;
  
  return apiRequest<UpdateBuildingResponse>(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete building by ID
 * @param id - Building ID
 * @returns Promise with deletion confirmation
 */
export async function deleteBuilding(id: string): Promise<DeleteBuildingResponse> {
  const url = `${API_BASE_URL}/buildings/${id}`;
  
  return apiRequest<DeleteBuildingResponse>(url, {
    method: 'DELETE',
  });
}

/**
 * Get building statistics
 * @param id - Building ID
 * @returns Promise with building statistics
 */
export async function getBuildingStats(id: string): Promise<GetBuildingStatsResponse> {
  const url = `${API_BASE_URL}/buildings/${id}/stats`;
  
  return apiRequest<GetBuildingStatsResponse>(url);
}

/**
 * Get buildings for selection/dropdown
 * @returns Promise with simplified buildings list
 */
export async function getBuildingsForSelection(): Promise<GetBuildingsForSelectionResponse> {
  const url = `${API_BASE_URL}/buildings/selection`;
  
  return apiRequest<GetBuildingsForSelectionResponse>(url);
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Validate building data
 * @param data - Building data to validate
 * @returns Validation result
 */
export function validateBuildingData(data: {
  name?: string;
  description?: string | null;
  isActive?: boolean;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (data.name !== undefined) {
    if (!data.name || typeof data.name !== 'string') {
      errors.push('Building name is required and must be a string');
    } else if (data.name.trim().length === 0) {
      errors.push('Building name cannot be empty');
    } else if (data.name.length > 100) {
      errors.push('Building name cannot exceed 100 characters');
    }
  }

  if (data.description !== undefined && data.description !== null) {
    if (typeof data.description !== 'string') {
      errors.push('Building description must be a string');
    } else if (data.description.length > 500) {
      errors.push('Building description cannot exceed 500 characters');
    }
  }

  if (data.isActive !== undefined && typeof data.isActive !== 'boolean') {
    errors.push('Building active status must be a boolean');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Format building for display
 * @param building - Building object
 * @returns Formatted building data
 */
export function formatBuildingForDisplay(building: BuildingWithStats): {
  id: string;
  name: string;
  description: string;
  isActive: string;
  floorsCount: string;
  roomsCount: string;
  holidaysCount: string;
  createdAt: string;
  updatedAt: string;
} {
  return {
    id: building.id,
    name: building.name,
    description: building.description || 'No description',
    isActive: building.isActive ? 'Active' : 'Inactive',
    floorsCount: building._count?.floors?.toString() || '0',
    roomsCount: (building.roomsCount || 0).toString(),
    holidaysCount: (building.holidaysCount || 0).toString(),
    createdAt: new Date(building.createdAt).toLocaleDateString(),
    updatedAt: new Date(building.updatedAt).toLocaleDateString(),
  };
}

/**
 * Get building status color
 * @param isActive - Building active status
 * @returns Status color
 */
export function getBuildingStatusColor(isActive: boolean): string {
  return isActive ? 'green' : 'red';
}

/**
 * Get building status icon
 * @param isActive - Building active status
 * @returns Status icon
 */
export function getBuildingStatusIcon(isActive: boolean): string {
  return isActive ? 'check' : 'x';
}
